import { ref, nextTick, onMounted, onBeforeUnmount, computed } from 'vue';
import type Draggable from 'gsap/Draggable';
import { useThrottleFn } from '@vueuse/core';

export function useSimpleCarousel() {
  const { $gsap: gsap, $Draggable: Draggable } = useNuxtApp();

  // Element
  const carousel = ref<HTMLElement | null>(null);

  //Reference to the draggable container
  const draggableInstance = ref<Draggable | null>(null);

  // GSAP context
  const gsapCtx = ref<gsap.Context | null>(null);

  // Sizes
  const maxOffset = ref(0); // Maximum X offset (when the carousel scrolls to end)

  // Important
  const currentIndex = ref(0);
  const snapPositions = ref<number[]>([]); // Array of X positions to snap to

  // carousel state
  const interacted = ref<boolean>(false); // did the user interact with the carousel?
  const dragging = ref<boolean>(false); // is the user currently dragging the carousel?
  const isSingleSlide = ref<boolean>(true);
  const toValue = ref<gsap.core.Tween | null>(null); // GSAP tween for the carousel
  // CSS Classes
  const activeClasses = ref<Array<string>>([]); // CSS classes

  /**
   * Retrieve the current X position of the draggable
   * @return number
   */
  const getCurrentX = () => {
    // Retrieve the current X position of the draggable track
    return gsap.getProperty(carousel.value, 'x') as number;
  };

  /**
   * Assign widths and calculate snap positions
   *
   * @param onResize boolean
   */
  const calculateDimensions = (onResize = false) => {
    if (carousel.value && carousel.value.children[0]) {
      // calculate an array of offsets based on each individual slide width
      let offset = 0;
      const offsets: number[] = [];

      Array.from(carousel.value.children).forEach((el) => {
        offsets.push(offset);
        const elWidth = el.clientWidth + parseFloat(window.getComputedStyle(carousel.value).gap);
        offset -= elWidth;
      });
      snapPositions.value = offsets;

      // Calculate maximum offset beyond which the carousel shouldn't scroll
      maxOffset.value = -carousel.value.scrollWidth + carousel.value.clientWidth;

      // If resizing, maintain the current index's position
      updateCurrentIndex(onResize);
      isSingleSlide.value = (carousel.value?.children?.length || 0) === 1;
    }
  };

  /**
   * Update the current index
   *
   * @param onResize boolean
   */
  const updateCurrentIndex = (onResize = false) => {
    updateButtonStates();

    if (!onResize) {
      // Find the nearest snap point and update the current index
      const currentX = getCurrentX();
      const differences = snapPositions.value.map((value) => Math.abs(value - currentX));
      const closestIndex = differences.indexOf(Math.min(...differences));

      currentIndex.value =
        closestIndex === -1 ? (carousel.value?.children.length || 0) - 1 : closestIndex;
    } else {
      // After resize, maintain the same current index but snap to its new calculated position
      const newOffset = snapPositions.value[currentIndex.value];
      gsap.set(carousel.value, { x: newOffset });
      goToItem(currentIndex.value);
    }
  };

  /**
   *  Move the carousel to a specific item (using GSAP for animation)
   *
   * @param index number
   */
  const goToItem = (index: number) => {
    const newOffset = snapPositions.value[index] ?? 0;
    const clampedOffset = Math.max(Math.min(newOffset, 0), maxOffset.value);

    if (!carousel.value) return;

    toValue.value = gsap.to(carousel.value, {
      x: clampedOffset,
      duration: 0.8,
      ease: 'power3.inOut',
      onUpdate: () => {
        currentIndex.value = index;
        updateButtonStates();
      },
      onComplete: () => {
        interacted.value = true;
      },
    });
  };

  /**
   *  Move the carousel to a specific item (using GSAP for animation)
   *
   * @param direction string
   */
  const handleNavigationClick = (direction: string) => {
    if (direction === 'prev' && currentIndex.value > 0) {
      goToItem(currentIndex.value - 1);
    } else if (
      direction === 'next' &&
      currentIndex.value < (carousel.value?.children?.length || 0) - 1
    ) {
      goToItem(currentIndex.value + 1);
    }
  };

  /**
   *  Update the navigation buttons
   *
   */
  const updateButtonStates = () => {
    const currentX = getCurrentX();

    const classes = [];

    if (currentX < 0 && currentX > maxOffset.value) {
      classes.push('is--active-both');
    } else if (currentX < 0) {
      classes.push('is--active-prev');
    } else if (currentX > maxOffset.value) {
      classes.push('is--active-next');
    }

    activeClasses.value = classes;
  };

  /**
   * Check if the carousel reached the bounds and snap if needed
   */
  const checkBoundaryAndSnap = () => {
    const currentX = getCurrentX();

    if (currentX <= maxOffset.value) {
      // If carousel cannot drag further left, snap to the last item
      goToItem((carousel.value?.children?.length || 0) - 1);
    } else if (currentX >= 0) {
      // Optional: You can implement behavior for the rightmost bound if needed
      goToItem(0); // Snap to the first item
    } else {
      // Otherwise, update the current index normally
      updateCurrentIndex();
    }
  };

  const initializeCarousel = () => {
    const [draggable] = Draggable.create(carousel.value, {
      type: 'x',
      bounds: { minX: maxOffset.value, maxX: 0 },
      dragClickables: true,
      allowContextMenu: true,
      zIndexBoost: false,
      inertia: true,
      edgeResistance: isSingleSlide.value || maxOffset.value >= 0 ? 1 : 0.85,
      snap: { x: (value) => gsap.utils.snap(snapPositions.value)(value) },
      onDragStart() {
        dragging.value = true;
        if (!interacted.value) interacted.value = true;
      },
      onDragEnd() {
        checkBoundaryAndSnap();
      },
      onThrowComplete() {
        checkBoundaryAndSnap();
        dragging.value = false;
      },
      onThrowUpdate() {
        updateCurrentIndex();
      },
    });

    draggableInstance.value = draggable || null;

    // Set the buttons and active states after Draggable is ready
    goToItem(currentIndex.value);
    updateButtonStates();
  };

  const onResizeHandler = () => {
    // Adjusts dimensions and positions when window resizes
    calculateDimensions(true);

    if (draggableInstance.value) {
      draggableInstance.value.applyBounds({ minX: maxOffset.value, maxX: 0 });
      draggableInstance.value.update();
    }

    goToItem(currentIndex.value); // After resizing, maintain the active item
    updateButtonStates();
  };

  const onFocus = (e) => {
    if (e.detail !== 0) {
      // triggered via click
      return;
    }

    const focussedSlide = e.target.closest('.slide');
    if (focussedSlide && carousel.value?.children) {
      const focusedSlideIndex = Array.from(carousel.value?.children).indexOf(focussedSlide);
      goToItem(focusedSlideIndex);
    }
  };

  // Lifecycle hooks
  onMounted(() => {
    nextTick(() => {
      gsapCtx.value = gsap.context(() => {
        calculateDimensions(); // Calculate dimensions on mount
        initializeCarousel(); // Set up draggable carousel

        /* there are situations where the slotted children (the slides in the carousel) aren't ready.
        this mostly seem to happening in a carousel with just images
        waiting for nextTick() isn't enough, this seems to work. */
        if (maxOffset.value === 0) {
          let count = 0;

          const carouselSizeCheck = window.setInterval(() => {
            update();
            count++;

            if (count === 3) {
              clearInterval(carouselSizeCheck);
            }
          }, 1000 / 30);
        }

        window.addEventListener('resize', useThrottleFn(onResizeHandler, 1000 / 30, true));
        carousel.value?.addEventListener('focusin', onFocus);
      }, carousel.value);
    });
  });

  onBeforeUnmount(() => {
    window.removeEventListener('resize', useThrottleFn(onResizeHandler, 1000 / 30, true));
    carousel.value?.removeEventListener('focusin', onFocus);

    draggableInstance.value?.kill(); // Clean up GSAP Draggable instance
    toValue.value?.kill(); // kill the to tween
    gsapCtx.value?.revert(); // clean up context
  });

  // get the currently active slide (when done dragging & after first interaction)
  const activeSlide = computed(() => (!dragging.value && interacted.value ? currentIndex : -1));

  // force an update
  const update = onResizeHandler;

  return {
    carousel,
    currentIndex,
    activeSlide,
    isSingleSlide,
    maxOffset,
    activeClasses,
    update,
    goToItem,
    handleNavigationClick,
  };
}
